import React, { useState } from "react";
import ValidationRules from "../components/retailer/tiles/ValidationRules";
import ProcessLogsTable from "../components/retailer/tiles/ProcessLogsTable";
import RetailerSidebar from "../components/retailer/RetailerSidebar";
import { bulkUploadProducts } from "../services/api";

export default function BulkProductUpload() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (!file.name.toLowerCase().endsWith(".csv")) {
      alert("Please select a CSV file.");
      e.target.value = "";
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleReset = () => {
    setSelectedFile(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert("Please select a CSV file first.");
      return;
    }

    try {
      setUploading(true);

      const token = localStorage.getItem("retailer_token");

      const result = await bulkUploadProducts(selectedFile, token);

      if (result.success) {
        alert(`Success!\n\n${result.created_count} products created.`);

        setSelectedFile(null);

        // fetchProducts();
        return;
      }

      if (result.errors?.length) {
        const errorMessage = result.errors
          .map((item) => `Row ${item.row}: ${item.error}`)
          .join("\n");

        alert(`${result.message}\n\n${errorMessage}`);

        return;
      }

      alert(result.message || "Bulk upload failed.");
    } catch (error) {
      console.error(error);

      alert(error.message || "Failed to upload CSV.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full overflow-hidden bg-gray-50">
      <div className="w-64 flex-shrink-0">
        <RetailerSidebar />
      </div>

      <div className="flex-1 min-w-0 p-6 bg-slate-50 min-h-screen overflow-y-auto font-sans">
        <h1 className="text-xl font-bold text-gray-800 mb-6">
          Bulk Product Upload
        </h1>
        {/* Top Section Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {" "}
          {/* Left Column: Instructions & Dropzone */}
          <div className="lg:col-span-7 space-y-6">
            {" "}
            {/* Instructions Box */}
            <div className="bg-red-50/40 border border-red-100 rounded-xl p-5">
              <h2 className="text-sm font-bold text-red-700 mb-1">
                How to Upload Products
              </h2>
              <p className="text-xs text-gray-500 mb-4">
                Follow these steps to upload products using a CSV file.
              </p>

              <div className="bg-amber-50 border border-amber-200/60 rounded-lg p-3 text-xs text-amber-900 mb-4">
                <strong>Note:</strong> If you want to add filters in CSV, first
                create them in Admin → Product Filters.
              </div>

              <ol className="space-y-4 text-xs text-gray-600">
                <li className="flex items-start justify-between gap-3">
                  <div className="flex gap-3">
                    <span className="w-5 h-5 rounded-full bg-slate-800 text-white flex items-center justify-center font-bold text-[10px]">
                      1
                    </span>
                    <div>
                      <strong className="text-gray-800 block text-xs">
                        Download the sample file
                      </strong>
                      <span className="text-gray-500">
                        Click Download CSV Example to see the correct format.
                      </span>
                    </div>
                  </div>
                  <a
                    href="/bulk_product_upload_example.csv"
                    download="bulk_product_upload_example.csv"
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded-md text-xs font-semibold hover:bg-indigo-700 transition-colors inline-flex items-center gap-1"
                  >
                    Download CSV Example ↓
                  </a>
                </li>

                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-slate-800 text-white flex items-center justify-center font-bold text-[10px]">
                    2
                  </span>
                  <div>
                    <strong className="text-gray-800 block text-xs">
                      Add your product details
                    </strong>
                    <span className="text-gray-500">
                      Replace the sample data with your own product data. Do not
                      change the header raw.
                    </span>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-slate-800 text-white flex items-center justify-center font-bold text-[10px]">
                    3
                  </span>
                  <div>
                    <strong className="text-gray-800 block text-xs">
                      Upload
                    </strong>
                    <span className="text-gray-500">
                      After adding your product details, upload the CSV file
                      below
                    </span>
                  </div>
                </li>
              </ol>
            </div>
            {/* Upload CSV Dropzone Card */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm space-y-4">
              <h3 className="text-xs font-bold text-gray-700">Upload CSV</h3>

              <div className="relative border-2 border-dashed border-gray-200 rounded-lg p-8 text-center bg-gray-50/50 hover:bg-gray-50 transition-colors">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
                <div className="w-10 h-10 bg-slate-700 text-white rounded-full flex items-center justify-center mx-auto mb-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>
                </div>
                <p className="text-xs font-medium text-gray-600 mb-4">
                  Drag and Drop file here or click to upload
                </p>

                <div className="max-w-md mx-auto flex border border-gray-300 rounded-md bg-white overflow-hidden text-xs">
                  <span className="px-4 py-2 bg-gray-100 font-semibold text-gray-700 border-r border-gray-300">
                    Choose File
                  </span>
                  <span className="px-4 py-2 text-gray-400 flex-1 text-left truncate">
                    {selectedFile ? selectedFile.name : "No file chosen"}
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  className="px-6 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-md hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? "Uploading..." : "Upload"}
                </button>
                <button
                  onClick={handleReset}
                  disabled={uploading}
                  className="px-6 py-2 bg-slate-600 text-white text-xs font-semibold rounded-md hover:bg-slate-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
          {/* Right Column: Validation Rules */}
          <div className="lg:col-span-5">
            <ValidationRules />
          </div>
        </div>
        {/* Process Logs Table Section */}
        {/* <ProcessLogsTable logs={[]} /> */}
      </div>
    </div>
  );
}
